import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { nodeApi, invalidateAuthorizedExamsCache } from '../services/api';
import { clearGuestQcmSession } from '../utils/guestSession';
import { ADAPTIVE_EXAM_ENTRY_KEY } from '../constants/adaptiveEntry';
import {
    DEFAULT_REVISION_STRATEGY,
    readRevisionStrategyFromStorage,
    saveRevisionStrategyToStorage,
} from '../utils/qcmInitialLoad';

/**
 * Décode (sans vérification cryptographique) la partie payload d'un JWT et
 * renvoie `true` si le token est expiré ou illisible. Évite de générer
 * 401/403 inutiles côté backend quand le token stocké est trop vieux.
 */
function isJwtExpired(token) {
    if (!token || typeof token !== 'string') return true;
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    try {
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (!payload?.exp) return false;
        const skewSec = 5;
        return Date.now() / 1000 >= Number(payload.exp) - skewSec;
    } catch (_) {
        return true;
    }
}

const AuthContext = createContext();

/** Profil API (Mongoose) : garantir `id` pour le front + localStorage (fetchAuthorizedExams). */
function normalizeProfileUser(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const id = raw.id ?? raw._id;
    const role = String(raw.role || '').toLowerCase();
    return {
        ...raw,
        ...(id != null ? { id: String(id) } : {}),
        isGuest: role === 'guest' || raw.isGuest === true,
    };
}

/**
 * Récupère un JWT pour le compte technique « guest » (compte sans habilitation,
 * scopé aux examens extract_free → seules les questions `free` sont servies).
 * Utilisé au boot quand aucun token actif n'est en localStorage : permet au
 * visiteur d'avoir une identité authentifiée sans saisir de credentials.
 */
async function fetchGuestToken() {
    const resp = await nodeApi.post('/api/auth/guest-login', {}, { headers: { Authorization: '' } });
    if (resp?.data?.success && resp.data.token) {
        return { token: resp.data.token, user: normalizeProfileUser(resp.data.user) };
    }
    throw new Error(resp?.data?.error || 'guest-login failed');
}

/** After login: default revision filter for the active exam + session only. */
function persistDefaultLeftTopicsRevisionStrategy(userLike) {
    const uid = String(userLike?.username || userLike?._id || userLike?.id || 'anonymous_user');
    const examId = String(userLike?.lastSelections?.examId || '').trim();
    if (!examId) return;
    const sessionId = String(userLike?.lastSelections?.sessionId || '').trim() || '111222';
    try {
        const stored = readRevisionStrategyFromStorage(uid, examId, sessionId);
        const ft = String(stored?.filterType || '').toLowerCase();
        if (!stored?.filterType || ft === 'random' || ft === '' || ft === 'none') {
            saveRevisionStrategyToStorage(uid, examId, sessionId, {
                ...DEFAULT_REVISION_STRATEGY,
                ...(stored || {}),
                filterType: 'left_topics',
                studyMode: stored?.studyMode || 'qcm',
            });
        }
    } catch (_) {
        /* ignore */
    }
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(() => {
        // Évite de réutiliser un JWT déjà expiré au premier render :
        // sinon la première requête /api/auth/profile renvoie 403 et casse
        // l'ouverture de page (cascade 401 sur /api/sessions/:id, etc.).
        const t = localStorage.getItem('auth_token');
        if (!t) return null;
        if (isJwtExpired(t)) {
            try {
                localStorage.removeItem('auth_token');
                localStorage.removeItem('token');
                localStorage.removeItem('user');
            } catch (_) { /* ignore */ }
            return null;
        }
        return t;
    });

    // Configurer l'intercepteur Axios pour inclure le token dans les requêtes
    useEffect(() => {
        const requestInterceptor = nodeApi.interceptors.request.use(
            (config) => {
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        const responseInterceptor = nodeApi.interceptors.response.use(
            (response) => response,
            (error) => {
                const status = error?.response?.status;
                const code = error?.response?.data?.error;
                // 401 = pas de token, 403 = token invalide/expiré
                // (cf. backend/routes/authRoutes.js#authenticateToken).
                // Dans les deux cas, purger la session locale pour éviter la
                // cascade d'erreurs (sessions, examens, profile…).
                //
                // Exception : 403 avec code `guest_read_only` est une réponse
                // métier (le guest tente une écriture qui lui est interdite)
                // et NE doit PAS déconnecter — sinon boucle de re-auto-login.
                if (status === 401 || (status === 403 && code !== 'guest_read_only')) {
                    logout();
                }
                return Promise.reject(error);
            }
        );

        return () => {
            nodeApi.interceptors.request.eject(requestInterceptor);
            nodeApi.interceptors.response.eject(responseInterceptor);
        };
    }, [token]);

    // Vérifier le token au chargement de l'application (éviter double appel en StrictMode)
    const prevTokenRef = useRef(null);
    const hasCheckedAuthRef = useRef(false);
    useEffect(() => {
        // Réinitialiser le garde lorsque le token change
        if (prevTokenRef.current !== token) {
            hasCheckedAuthRef.current = false;
            prevTokenRef.current = token;
        }

        const checkAuthStatus = async () => {
            if (!token) {
                // Aucun JWT en localStorage → on demande un token « guest » au backend.
                // Le visiteur a ainsi une identité authentifiée scopée aux examens
                // extract_free (seules les questions `free` lui sont servies).
                try {
                    const guest = await fetchGuestToken();
                    if (guest?.token) {
                        localStorage.setItem('auth_token', guest.token);
                        localStorage.setItem('user', JSON.stringify(guest.user));
                        setUser(guest.user);
                        setToken(guest.token);
                        console.log('🟢 Auto-login guest: token public obtenu');
                    }
                } catch (e) {
                    console.warn('Guest auto-login failed:', e?.message || e);
                } finally {
                    setLoading(false);
                }
                return;
            }

            setLoading(true);
            try {
                const response = await nodeApi.get('/api/auth/profile');
                if (response.data.success) {
                    const userData = normalizeProfileUser(response.data.user);
                    setUser(userData);
                    // ⚠️ IMPORTANT: Sauvegarder le profil dans localStorage pour fetchAuthorizedExams
                    localStorage.setItem('user', JSON.stringify(userData));
                    persistDefaultLeftTopicsRevisionStrategy(userData);
                    console.log('✅ Profil utilisateur chargé et sauvegardé:', userData.username);
                } else {
                    // Token invalide
                    logout();
                }
            } catch (error) {
                console.error('Auth check failed:', error);
                logout();
            } finally {
                setLoading(false);
            }
        };

        if (!hasCheckedAuthRef.current) {
            hasCheckedAuthRef.current = true;
            checkAuthStatus();
        }
    }, [token]);

    // Gestion de l'authentification LinkedIn via URL
    useEffect(() => {
        const handleLinkedInCallback = () => {
            const urlParams = new URLSearchParams(window.location.search);
            const token = urlParams.get('token');
            const error = urlParams.get('error');

            if (error) {
                console.error('LinkedIn authentication error:', error);
                // Nettoyer l'URL
                window.history.replaceState({}, document.title, window.location.pathname);
                return;
            }

            if (token) {
                // Token reçu de LinkedIn, l'enregistrer et rediriger
                setToken(token);
                setLoading(true);
                localStorage.setItem('auth_token', token);
                
                // Nettoyer l'URL
                window.history.replaceState({}, document.title, window.location.pathname);
                
                // Vérifier l'authentification avec le nouveau token
                const checkLinkedInAuth = async () => {
                    try {
                        const response = await nodeApi.get('/api/auth/profile', {
                            headers: {
                                'Authorization': `Bearer ${token}`
                            }
                        });
                        if (response.data.success) {
                            const userData = normalizeProfileUser(response.data.user);
                            setUser(userData);
                            // ⚠️ IMPORTANT: Sauvegarder le profil dans localStorage pour fetchAuthorizedExams
                            localStorage.setItem('user', JSON.stringify(userData));
                            persistDefaultLeftTopicsRevisionStrategy(userData);
                            console.log('✅ Profil utilisateur chargé (LinkedIn) et sauvegardé:', userData.username);
                        } else {
                            logout();
                        }
                    } catch (error) {
                        console.error('LinkedIn auth check failed:', error);
                        logout();
                    } finally {
                        setLoading(false);
                    }
                };
                
                checkLinkedInAuth();
            }
        };

        handleLinkedInCallback();
    }, []);

    const login = async (username, password) => {
        try {
            const response = await nodeApi.post('/api/auth/login', {
                username,
                password
            });

            if (response.data.success) {
                const { token: newToken, user: userData } = response.data;
                const normalized = normalizeProfileUser(userData);

                setLoading(true);
                setToken(newToken);
                setUser(normalized);
                localStorage.setItem('auth_token', newToken);
                // ⚠️ IMPORTANT: Sauvegarder le profil dans localStorage pour fetchAuthorizedExams
                localStorage.setItem('user', JSON.stringify(normalized));
                persistDefaultLeftTopicsRevisionStrategy(normalized);
                console.log('✅ Profil utilisateur chargé (login) et sauvegardé:', normalized?.username);
                clearGuestQcmSession();
                try {
                    localStorage.setItem(ADAPTIVE_EXAM_ENTRY_KEY, 'picker');
                } catch {
                    /* ignore */
                }
                window.dispatchEvent(new CustomEvent('auth_logged_in'));

                return { success: true };
            } else {
                return { success: false, error: response.data.error };
            }
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Login failed';
            return { success: false, error: errorMessage };
        }
    };

    const register = async (userData) => {
        try {
            const response = await nodeApi.post('/api/auth/register', userData);

            if (response.data.success) {
                const { token: newToken, user: newUser } = response.data;
                const normalized = normalizeProfileUser(newUser);

                setLoading(true);
                setToken(newToken);
                setUser(normalized);
                localStorage.setItem('auth_token', newToken);
                localStorage.setItem('user', JSON.stringify(normalized));
                persistDefaultLeftTopicsRevisionStrategy(normalized);
                clearGuestQcmSession();
                try {
                    localStorage.setItem(ADAPTIVE_EXAM_ENTRY_KEY, 'picker');
                } catch {
                    /* ignore */
                }
                window.dispatchEvent(new CustomEvent('auth_logged_in'));

                return { success: true };
            } else {
                return { success: false, error: response.data.error };
            }
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Registration failed';
            return { success: false, error: errorMessage };
        }
    };

    const logout = () => {
        setToken(null);
        setUser(null);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        clearGuestQcmSession();
        invalidateAuthorizedExamsCache();
        try {
            localStorage.setItem(ADAPTIVE_EXAM_ENTRY_KEY, 'picker');
        } catch {
            /* ignore */
        }
        window.dispatchEvent(new CustomEvent('auth_logged_out'));

        // Après déconnexion, on retombe immédiatement en session « guest » :
        // l'app a toujours une identité authentifiée minimale, et le visiteur
        // peut continuer à explorer les examens extract_free sans friction.
        fetchGuestToken()
            .then((guest) => {
                if (guest?.token) {
                    localStorage.setItem('auth_token', guest.token);
                    localStorage.setItem('user', JSON.stringify(guest.user));
                    setUser(guest.user);
                    setToken(guest.token);
                }
            })
            .catch((e) => console.warn('Guest re-login after logout failed:', e?.message || e));
    };

    const updateProfile = async (profileData) => {
        try {
            const response = await nodeApi.put('/api/auth/profile', profileData);

            if (response.data.success) {
                const normalized = normalizeProfileUser(response.data.user);
                setUser(normalized);
                localStorage.setItem('user', JSON.stringify(normalized));
                return { success: true };
            } else {
                return { success: false, error: response.data.error };
            }
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Profile update failed';
            return { success: false, error: errorMessage };
        }
    };

    const saveSelections = async (selections) => {
        try {
            const response = await nodeApi.put('/api/auth/selections', selections);

            if (response.data.success) {
                setUser((prev) => {
                    const merged = {
                        ...(prev || {}),
                        lastSelections: response.data.selections,
                    };
                    try {
                        localStorage.setItem('user', JSON.stringify(merged));
                    } catch (_) {
                        /* ignore */
                    }
                    return merged;
                });
                return { success: true };
            } else {
                return { success: false, error: response.data.error };
            }
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Failed to save selections';
            return { success: false, error: errorMessage };
        }
    };

    const getAuthorizedExams = async () => {
        try {
            const response = await nodeApi.get('/api/auth/examens');
            
            if (response.data.success) {
                return { success: true, examens: response.data.examens };
            } else {
                return { success: false, error: response.data.error };
            }
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Failed to get authorized exams';
            return { success: false, error: errorMessage };
        }
    };

    const getAuthorizedStrategies = async () => {
        try {
            const response = await nodeApi.get('/api/auth/strategies');
            
            if (response.data.success) {
                return { success: true, strategies: response.data.strategies };
            } else {
                return { success: false, error: response.data.error };
            }
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Failed to get authorized strategies';
            return { success: false, error: errorMessage };
        }
    };

    const forgotPassword = async (email) => {
        try {
            const response = await nodeApi.post('/api/auth/forgot-password', { email });
            return { 
                success: true, 
                message: response.data.message,
                resetToken: response.data.resetToken // En développement seulement
            };
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Reset password request failed';
            return { success: false, error: errorMessage };
        }
    };

    const resetPassword = async (token, newPassword) => {
        try {
            const response = await nodeApi.post('/api/auth/reset-password', {
                token,
                newPassword
            });
            
            if (response.data.success) {
                return { success: true, message: response.data.message };
            } else {
                return { success: false, error: response.data.error };
            }
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Password reset failed';
            return { success: false, error: errorMessage };
        }
    };

    // Logique simple pour identifier les admins (basée sur le nom d'utilisateur)
    const isAdmin = user && (user.username === 'admin' || user.role === 'admin' || user.is_admin === true);
    const isJagE = user && String(user.username || '').trim().toLowerCase() === 'jag-e';
    const isGuest = !!user && (user.isGuest === true || user.role === 'guest');
    // `isAuthenticated` reste basé sur la présence d'un *vrai* compte (non-guest)
    // pour préserver la sémantique attendue par tout le code applicatif : les
    // composants qui gating sur `isAuthenticated` traitent le guest auto-loginé
    // comme un visiteur non-connecté (bouton « Login » visible, contenu payant
    // masqué). L'intercepteur Axios continue à utiliser `token` (incluant le
    // JWT guest) pour authentifier les appels API.
    const isAuthenticated = !!user && !isGuest;

    const value = {
        user,
        loading,
        isAuthenticated,
        isAdmin,
        isJagE,
        isGuest,
        login,
        logout,
        register,
        updateProfile,
        saveSelections,
        getAuthorizedExams,
        getAuthorizedStrategies,
        forgotPassword,
        resetPassword,
        setToken,
        setUser,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}; 